/**
 * Embed Theme Provider
 * Provides theme context and utilities to all embed components
 */

import React, { createContext, useContext, useMemo } from 'react';

import { createTheme, DEFAULT_PRIMARY_COLOR } from './defaultTheme';
import {
  avatarStyles,
  cardStyles,
  dividerStyles,
  errorStyles,
  flexStyles,
  generateGlobalStyles,
  headerStyles,
  headingStyles,
  inputStyles,
  mergeStyles,
  messageBubbleStyles,
  modalContainerStyles,
  modalOverlayStyles,
  pageContainerStyles,
  primaryButtonStyles,
  secondaryButtonStyles,
  spinnerStyles,
  successStyles,
  textStyles,
  withHoverStyles,
} from './themeUtils';
import { EmbedTheme, EmbedThemeConfig } from './types';

// ============================================================================
// Context Types
// ============================================================================

interface ThemeContextValue {
  /** Current theme object */
  theme: EmbedTheme;
  /** Whether dark mode is active */
  isDark: boolean;
  /** Primary color value */
  primaryColor: string;

  // Style generators bound to current theme
  styles: {
    page: () => React.CSSProperties;
    card: (props?: { elevated?: boolean }) => React.CSSProperties;
    primaryButton: (props?: {
      size?: 'sm' | 'md' | 'lg';
      disabled?: boolean;
      fullWidth?: boolean;
    }) => React.CSSProperties;
    secondaryButton: (props?: { size?: 'sm' | 'md' | 'lg'; disabled?: boolean }) => React.CSSProperties;
    input: (props?: { hasError?: boolean }) => React.CSSProperties;
    avatar: (props?: {
      size?: 'sm' | 'md' | 'lg' | 'xl';
      shape?: 'circle' | 'rounded-sm';
    }) => React.CSSProperties;
    heading: (props?: { level?: 1 | 2 | 3 | 4 }) => React.CSSProperties;
    text: (props?: {
      variant?: 'primary' | 'secondary' | 'tertiary';
      size?: 'xs' | 'sm' | 'base' | 'lg';
    }) => React.CSSProperties;
    flex: (props?: {
      direction?: 'row' | 'column';
      align?: 'start' | 'center' | 'end' | 'stretch';
      justify?: 'start' | 'center' | 'end' | 'between' | 'around';
      gap?: '0.5' | '1' | '1.5' | '2' | '2.5' | '3' | '4' | '5' | '6' | '8';
      wrap?: boolean;
    }) => React.CSSProperties;
    spinner: (props?: { size?: number }) => React.CSSProperties;
    divider: (props?: { vertical?: boolean }) => React.CSSProperties;
    error: () => React.CSSProperties;
    success: () => React.CSSProperties;
    header: () => React.CSSProperties;
    messageBubble: (props?: { isUser?: boolean }) => React.CSSProperties;
    modalOverlay: () => React.CSSProperties;
    modalContainer: () => React.CSSProperties;
  };

  // Utility functions
  utils: {
    merge: (...styles: (React.CSSProperties | undefined)[]) => React.CSSProperties;
    withHover: (
      baseStyles: React.CSSProperties,
      hoverStyles: React.CSSProperties,
    ) => {
      style: React.CSSProperties;
      onMouseEnter: (e: React.MouseEvent<HTMLElement>) => void;
      onMouseLeave: (e: React.MouseEvent<HTMLElement>) => void;
    };
    globalStyles: string;
  };
}

// ============================================================================
// Context
// ============================================================================

const ThemeContext = createContext<ThemeContextValue | null>(null);

// ============================================================================
// Provider Props
// ============================================================================

export interface EmbedThemeProviderProps {
  children: React.ReactNode;
  /** Theme configuration */
  config?: EmbedThemeConfig;
  /** Override: theme mode */
  mode?: 'light' | 'dark';
  /** Override: primary color */
  primaryColor?: string;
}

// ============================================================================
// Provider Component
// ============================================================================

export const EmbedThemeProvider: React.FC<EmbedThemeProviderProps> = ({
  children,
  config,
  mode: modeProp,
  primaryColor: primaryColorProp,
}) => {
  // Determine final mode and color
  const mode = modeProp ?? config?.mode ?? 'light';
  const primaryColor = primaryColorProp ?? config?.primaryColor ?? DEFAULT_PRIMARY_COLOR;

  // Create theme object
  const theme = useMemo(() => {
    const baseTheme = createTheme(mode, primaryColor);

    // Apply custom font family if provided
    if (config?.fontFamily) {
      baseTheme.typography.fontFamily = config.fontFamily;
    }

    // Apply custom border radius scale if provided
    if (config?.borderRadius) {
      const radiusScale = {
        none: { sm: '0px', base: '0px', md: '0px', lg: '0px', xl: '0px', '2xl': '0px' },
        sm: { sm: '2px', base: '4px', md: '6px', lg: '8px', xl: '10px', '2xl': '12px' },
        md: { sm: '4px', base: '6px', md: '8px', lg: '12px', xl: '16px', '2xl': '20px' },
        lg: { sm: '6px', base: '8px', md: '12px', lg: '16px', xl: '20px', '2xl': '24px' },
        full: { sm: '9999px', base: '9999px', md: '9999px', lg: '9999px', xl: '9999px', '2xl': '9999px' },
      };
      const scale = radiusScale[config.borderRadius];
      baseTheme.borderRadius = { ...baseTheme.borderRadius, ...scale };
    }

    // Apply custom color overrides
    if (config?.colors?.background) {
      baseTheme.colors.background = { ...baseTheme.colors.background, ...config.colors.background };
    }
    if (config?.colors?.text) {
      baseTheme.colors.text = { ...baseTheme.colors.text, ...config.colors.text };
    }
    if (config?.colors?.border) {
      baseTheme.colors.border = { ...baseTheme.colors.border, ...config.colors.border };
    }

    return baseTheme;
  }, [mode, primaryColor, config]);

  // Create bound style generators
  const styles = useMemo(
    () => ({
      page: () => pageContainerStyles(theme),
      card: (props?: { elevated?: boolean }) => cardStyles(theme, props),
      primaryButton: (props?: { size?: 'sm' | 'md' | 'lg'; disabled?: boolean; fullWidth?: boolean }) =>
        primaryButtonStyles(theme, props),
      secondaryButton: (props?: { size?: 'sm' | 'md' | 'lg'; disabled?: boolean }) =>
        secondaryButtonStyles(theme, props),
      input: (props?: { hasError?: boolean }) => inputStyles(theme, props),
      avatar: (props?: { size?: 'sm' | 'md' | 'lg' | 'xl'; shape?: 'circle' | 'rounded-sm' }) =>
        avatarStyles(theme, props),
      heading: (props?: { level?: 1 | 2 | 3 | 4 }) => headingStyles(theme, props),
      text: (props?: {
        variant?: 'primary' | 'secondary' | 'tertiary';
        size?: 'xs' | 'sm' | 'base' | 'lg';
      }) => textStyles(theme, props),
      flex: (props?: {
        direction?: 'row' | 'column';
        align?: 'start' | 'center' | 'end' | 'stretch';
        justify?: 'start' | 'center' | 'end' | 'between' | 'around';
        gap?: '0.5' | '1' | '1.5' | '2' | '2.5' | '3' | '4' | '5' | '6' | '8';
        wrap?: boolean;
      }) => flexStyles(theme, props),
      spinner: (props?: { size?: number }) => spinnerStyles(theme, props),
      divider: (props?: { vertical?: boolean }) => dividerStyles(theme, props),
      error: () => errorStyles(theme),
      success: () => successStyles(theme),
      header: () => headerStyles(theme),
      messageBubble: (props?: { isUser?: boolean }) => messageBubbleStyles(theme, props),
      modalOverlay: () => modalOverlayStyles(theme),
      modalContainer: () => modalContainerStyles(theme),
    }),
    [theme],
  );

  // Create utilities
  const globalStyles = useMemo(() => generateGlobalStyles(theme), [theme]);

  const utils = useMemo(
    () => ({
      merge: mergeStyles,
      withHover: withHoverStyles,
      globalStyles,
    }),
    [globalStyles],
  );

  // Context value
  const value = useMemo<ThemeContextValue>(
    () => ({
      theme,
      isDark: mode === 'dark',
      primaryColor,
      styles,
      utils,
    }),
    [theme, mode, primaryColor, styles, utils],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

// ============================================================================
// Hook
// ============================================================================

// Default fallback theme for when context is not available
const createFallbackContext = (): ThemeContextValue => {
  const theme = createTheme('light', DEFAULT_PRIMARY_COLOR);
  return {
    theme,
    isDark: false,
    primaryColor: DEFAULT_PRIMARY_COLOR,
    styles: {
      page: () => pageContainerStyles(theme),
      card: (props) => cardStyles(theme, props),
      primaryButton: (props) => primaryButtonStyles(theme, props),
      secondaryButton: (props) => secondaryButtonStyles(theme, props),
      input: (props) => inputStyles(theme, props),
      avatar: (props) => avatarStyles(theme, props),
      heading: (props) => headingStyles(theme, props),
      text: (props) => textStyles(theme, props),
      divider: () => dividerStyles(theme),
      flex: (props) => flexStyles(props),
      spinner: (props) => spinnerStyles(theme, props),
      error: () => errorStyles(theme),
      success: () => successStyles(theme),
      header: () => headerStyles(theme),
      messageBubble: (props) => messageBubbleStyles(theme, props),
      modalOverlay: () => modalOverlayStyles(theme),
      modalContainer: (props) => modalContainerStyles(theme, props),
    },
    utils: {
      merge: mergeStyles,
      withHover: withHoverStyles,
      globalStyles: generateGlobalStyles(theme),
    },
  };
};

export const useEmbedTheme = (): ThemeContextValue => {
  const context = useContext(ThemeContext);
  if (!context) {
    // Return fallback context instead of throwing
    console.warn('[EmbedTheme] useEmbedTheme called outside of EmbedThemeProvider, using fallback theme');
    return createFallbackContext();
  }
  return context;
};

// ============================================================================
// Helper Hook for accessing just the theme object
// ============================================================================

export const useTheme = (): EmbedTheme => {
  const { theme } = useEmbedTheme();
  return theme;
};

// ============================================================================
// Helper Hook for checking dark mode
// ============================================================================

export const useIsDark = (): boolean => {
  const { isDark } = useEmbedTheme();
  return isDark;
};
